import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Edit, Plus, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import {
  ActionMenu,
  type ActionMenuItem,
  UniversalModuleView,
  IconButton,
} from '../../../ui-components/listing';
import { API_CONSTANTS } from '../../../utils/constants';

import SystemEmailFormModal from './SystemEmailFormModal';

import type { ColDef } from 'ag-grid-community';

interface SystemEmailRecord {
  emailTemplateId: number;
  emailTitle: string;
  emailCode: string;
  emailSubject?: string;
  status?: string;
}

export interface SystemEmailPageProps {
  initialModal?: { mode: 'create' | 'edit'; emailTemplateId?: string };
  modalReturnTo?: string;
}

const SystemEmailPage: React.FC<SystemEmailPageProps> = ({
  initialModal,
  modalReturnTo,
}) => {
  const navigate = useNavigate();

  const [refreshToken, setRefreshToken] = useState(0);
  const [isFormOpen, setIsFormOpen] = useState(Boolean(initialModal));
  const [formMode, setFormMode] = useState<'create' | 'edit'>(
    initialModal?.mode ?? 'create'
  );
  const [formId, setFormId] = useState<string | undefined>(
    initialModal?.emailTemplateId
  );

  useEffect(() => {
    if (!initialModal) return;
    setIsFormOpen(true);
    setFormMode(initialModal.mode);
    setFormId(initialModal.emailTemplateId);
  }, [initialModal]);

  const openCreateEmail = useCallback(() => {
    setFormMode('create');
    setFormId(undefined);
    setIsFormOpen(true);
  }, []);

  const openEditEmail = useCallback((emailTemplateId?: number) => {
    if (!emailTemplateId) return;
    setFormMode('edit');
    setFormId(String(emailTemplateId));
    setIsFormOpen(true);
  }, []);

  const buildActions = useCallback(
    (row: SystemEmailRecord) => {
      const actions: ActionMenuItem[] = [];
      if (row.emailTemplateId) {
        actions.push({
          label: 'Edit',
          icon: Edit,
          onClick: () => openEditEmail(row.emailTemplateId),
        });
      }
      return actions;
    },
    [openEditEmail]
  );

  const EmailTitleCellRenderer = (params: { data?: SystemEmailRecord }) => {
    const row = params.data;
    if (!row) return '-';
    return (
      <button
        type="button"
        onClick={() => openEditEmail(row.emailTemplateId)}
        className="text-brand-600 hover:underline hover:text-brand-800 cursor-pointer transition font-semibold truncate"
      >
        {row.emailTitle || '-'}
      </button>
    );
  };

  const ActionsCellRenderer = (params: { data?: SystemEmailRecord }) => {
    if (!params.data) return null;
    return (
      <ActionMenu
        items={buildActions(params.data)}
        showIcons
        buttonClassName="hover:bg-neutral-100 dark:hover:bg-neutral-800"
        iconClassName="text-neutral-600 dark:text-neutral-400"
      />
    );
  };

  const systemEmailColumns = useMemo<ColDef<SystemEmailRecord>[]>(
    () => [
      {
        headerName: 'Title',
        field: 'emailTitle',
        headerClass: 'font-bold',
        sortable: false,
        minWidth: 220,
        cellClass: 'min-w-0 truncate',
        cellRenderer: EmailTitleCellRenderer,
      },
      {
        headerName: 'Code',
        field: 'emailCode',
        sortable: false,
        headerClass: 'font-bold',
        minWidth: 180,
        cellClass: 'min-w-0 truncate',
      },
      {
        headerName: 'Subject',
        field: 'emailSubject',
        sortable: false,
        headerClass: 'font-bold',
        minWidth: 260,
        cellClass: 'min-w-0 truncate',
      },
      {
        headerName: 'Action',
        field: '__actions',
        sortable: false,
        width: 80,
        headerClass: 'justify-end text-right',
        cellClass: 'justify-end text-right',
        cellRenderer: ActionsCellRenderer,
      },
    ],
    []
  );

  return (
    <>
      <UniversalModuleView<SystemEmailRecord>
        moduleKey="system-emails"
        title="System Email"
        apiEndpoint={API_CONSTANTS.EMAIL.SYSTEM_EMAIL_LIST_URL}
        columnsConfig={systemEmailColumns}
        rowKey={(row) => row.emailTemplateId}
        tableRowRenderer={undefined}
        refreshToken={refreshToken}
        primaryAction={{
          label: 'Add Email',
          icon: Plus,
          onClick: openCreateEmail,
        }}
        extraActions={({ reset }) => (
          <IconButton icon={RefreshCw} title="Refresh" onClick={reset} />
        )}
        searchPlaceholder="Search emails by title or code..."
      />

      <SystemEmailFormModal
        isOpen={isFormOpen}
        mode={formMode}
        emailTemplateId={formId}
        onClose={() => {
          setIsFormOpen(false);
          setFormId(undefined);
          if (modalReturnTo) {
            navigate(modalReturnTo);
          }
        }}
        onSuccess={() => setRefreshToken((prev) => prev + 1)}
      />
    </>
  );
};

export default SystemEmailPage;
